/**
 * The figure an answer CITES for each foundation datapoint, read out of the answer's own HTML.
 *
 * A foundation citation (ESG-4014) carries the figure as written — `data-foundation_figure="3333 tCO2e"` —
 * beside `data-foundation_uid` and `data-foundation_version`. That text is the only record of an archived
 * version's value: `GET /years/{year}/curated` excludes archived rows. So the stale-figure notice reads the
 * used figure from here and only the LATEST figure from the server.
 *
 * Keyed `${uid}|${version}`, because one answer can cite two versions of one datapoint. Attribute values
 * are HTML-escaped in the stored answer; the common entities are undone so "&amp;" does not reach the page.
 *
 * Standalone (not in the `src/utils` barrel) so it can carry its own vitest.
 */
const CITE_TAG = /<cite\b([^>]*)>/gi;
const ATTR = /([\w-]+)\s*=\s*("([^"]*)"|'([^']*)')/g;

const unescape = (text) =>
  text
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");

export function citedFiguresByVersion(answerHtml) {
  const figures = {};
  if (typeof answerHtml !== "string" || !answerHtml) return figures;
  for (const tag of answerHtml.matchAll(CITE_TAG)) {
    const attrs = {};
    for (const attr of tag[1].matchAll(ATTR)) attrs[attr[1].toLowerCase()] = attr[3] ?? attr[4] ?? "";
    const uid = attrs["data-foundation_uid"];
    const figure = attrs["data-foundation_figure"];
    if (!uid || !figure) continue;
    const version = Number.parseInt(attrs["data-foundation_version"], 10);
    const key = `${uid}|${Number.isFinite(version) ? version : ""}`;
    if (!(key in figures)) figures[key] = unescape(figure);
  }
  return figures;
}

/** The cited figure for one usage entry, or null when the answer text carries none. */
export function citedFigureFor(figures, datapointUid, version) {
  if (!figures || !datapointUid) return null;
  return figures[`${datapointUid}|${version ?? ""}`] ?? null;
}
